'use client'

import { useState } from 'react'
import { Button } from '@mantine/core'
import { useRouter, useSearchParams } from 'next/navigation'
import { trackEvent, SegmentEvents, trackStoreEvent, StoreAnalytics, PageLocation } from '@nile/mantine-theme/analytics'
import { startLatencyTimer } from '@/lib/analytics'
import { NewStorefrontEvents, trackNewStorefrontEvent } from '@/lib/analytics/newStorefrontEvents'
import { skuBookingRoutes, SKU_BOOKING_REF_QUERY } from '@/lib/routes/skuBooking'
import classes from './RequestPrescriptionCta.module.css'

interface RequestPrescriptionCtaProps {
    skuId: number
    skuHyphenatedName: string
    productName: string
}

/**
 * Redirects into the existing SKU booking/questionnaire flow using the selected dose
 * form's medication_option SKU — the same entry point `/s` pages use, so questionnaire
 * selection and visit creation don't need to change for the PDP to book a visit.
 */
export function RequestPrescriptionCta({ skuId, skuHyphenatedName, productName }: RequestPrescriptionCtaProps) {
    const router = useRouter()
    const searchParams = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)

    const handleClick = () => {
        trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
            location: PageLocation.DETAIL_PAGE,
            sku_id: skuId,
            sku_name: productName,
        })
        trackEvent(SegmentEvents.SKU_PAGE_CTA_CLICKED, {
            sku_id: skuId,
            button_type: 'get_started',
            variant: 'pdp',
        })
        trackNewStorefrontEvent(NewStorefrontEvents.REQUEST_MEDICATION_CLICKED, {
            sku_id: skuId,
            variant: 'pdp',
        })

        startLatencyTimer('get_started_to_next_screen')
        setIsLoading(true)
        const ref = searchParams.get(SKU_BOOKING_REF_QUERY)
        router.push(skuBookingRoutes.screen(skuHyphenatedName, String(skuId), { ref }))
    }

    return (
        <Button
            onClick={handleClick}
            loading={isLoading}
            radius="xl"
            fullWidth
            h={52}
            maw={407}
            fz={16}
            fw="var(--gm-weight-text, 450)"
            className={classes.button}
        >
            Request medication
        </Button>
    )
}
